import { useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Group,
  Paper,
  ScrollArea,
  Select,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconArrowLeft, IconBuilding, IconSearch } from '@tabler/icons-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Conversation, User } from '~api';
import { api } from '~portal/api';
import { QuotaCard } from '~portal/components/admin/QuotaCard';
import { EditLimitsButton } from '~portal/components/admin/QuotaOverrideEditor';
import { ConversationList } from '~portal/components/inspector/ConversationList';
import { CopyIdButton } from '~portal/components/inspector/CopyIdButton';
import { UserOrganisation } from '~portal/components/inspector/UserOrganisation';
import { UserStatsStrip } from '~portal/components/inspector/UserStatsStrip';
import { logger } from '~portal/lib';
import { creditsSourceOf } from '~portal/lib/usage';

/**
 * @desc The inspector's shareable state: whose conversations are listed — one user's, or every
 * member's of one organisation (the usage overview's drill-down, H2-266). A user wins if both
 * arrive; the two are never shown together.
 */
type InspectorSearch = { userId?: string; orgId?: string };

export const Route = createFileRoute('/_protected/admin/inspector')({
  validateSearch: (search: Record<string, unknown>): InspectorSearch => {
    if (typeof search.userId === 'string' && search.userId) return { userId: search.userId };
    if (typeof search.orgId === 'string' && search.orgId) return { orgId: search.orgId };
    return {};
  },
  component: AdminInspectorPage,
});

const CONVERSATION_LIMIT = 100;

/** @desc One picker row per user: `users.list` is per membership, so a user can appear twice. */
const distinctUsers = (rows: User.Managed[]): User.Managed[] => {
  const seen = new Set<string>();
  return rows.filter((row) => {
    const key = row.userId ?? row.workosUserId;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const matches = (row: User.Managed, needle: string) =>
  row.email.toLowerCase().includes(needle) || (row.name ?? '').toLowerCase().includes(needle);

function AdminInspectorPage() {
  const { userId, orgId } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const show = (next: InspectorSearch) => {
    navigate({ search: next }).catch((err) => {
      logger.error('[AdminInspector] Navigation failed', {
        error: err instanceof Error ? err.message : String(err),
      });
    });
  };

  return (
    <Stack gap="lg" p="lg">
      <div>
        <Title order={1}>Inspector</Title>
        <Text c="dimmed" size="sm" mt={4}>
          Pick a user to list their conversations, then open one to see each turn&apos;s model
          calls, tool calls, timings and token usage as a waterfall. Read-only.
        </Text>
      </div>
      {userId ? (
        <UserConversations userId={userId} onBack={() => show({})} />
      ) : orgId ? (
        <OrganisationConversations orgId={orgId} onBack={() => show({})} />
      ) : (
        <UserPicker
          onSelect={(next) => show({ userId: next })}
          onSelectOrganisation={(next) => show({ orgId: next })}
        />
      )}
    </Stack>
  );
}

function UserPicker({
  onSelect,
  onSelectOrganisation,
}: {
  onSelect: (userId: string) => void;
  onSelectOrganisation: (orgId: string) => void;
}) {
  const [filter, setFilter] = useState('');
  const usersQuery = api.users.list.useQuery();
  const organisationsQuery = api.organisations.list.useQuery();
  const organisationOptions = (organisationsQuery.data ?? [])
    .map((org) => ({ value: org.id, label: org.name }))
    .sort((a, b) => a.label.localeCompare(b.label, 'en-GB'));

  const users = useMemo(() => distinctUsers(usersQuery.data ?? []), [usersQuery.data]);
  const needle = filter.trim().toLowerCase();
  const visible = needle ? users.filter((row) => matches(row, needle)) : users;

  return (
    <Paper withBorder p="md">
      <Stack gap="md">
        <Group gap="sm" wrap="wrap">
          <TextInput
            aria-label="Filter users"
            placeholder="Filter by email or name"
            leftSection={<IconSearch size={16} />}
            value={filter}
            onChange={(event) => setFilter(event.currentTarget.value)}
            w={320}
          />
          <Select
            aria-label="Organisation"
            placeholder="Or every conversation in an organisation"
            leftSection={<IconBuilding size={16} />}
            data={organisationOptions}
            value={null}
            onChange={(next) => {
              if (next) onSelectOrganisation(next);
            }}
            searchable
            nothingFoundMessage="No matching organisation"
            w={320}
          />
        </Group>
        {usersQuery.isError ? (
          <Alert color="red" title="Could not load users">
            {usersQuery.error.message}
          </Alert>
        ) : usersQuery.isLoading ? (
          <Skeleton height={160} />
        ) : visible.length === 0 ? (
          <Text c="dimmed" size="sm">
            {needle ? `No users match “${filter.trim()}”.` : 'No users.'}
          </Text>
        ) : (
          <ScrollArea>
            <Table striped highlightOnHover miw={700}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Email</Table.Th>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Organisation</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {visible.map((row) => {
                  const { userId } = row;
                  const button = (
                    <Button
                      size="compact-xs"
                      variant="light"
                      aria-label={`Inspect ${row.email}`}
                      disabled={!userId}
                      onClick={userId ? () => onSelect(userId) : undefined}
                    >
                      Conversations
                    </Button>
                  );

                  return (
                    <Table.Tr key={row.workosUserId} data-testid={`user-${row.workosUserId}`}>
                      <Table.Td>
                        <Text size="sm">{row.email}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{row.name ?? '—'}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{row.organisation.name}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge size="sm" variant="light" color="gray">
                          {row.status}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        {userId ? (
                          button
                        ) : (
                          <Tooltip label="No portal account yet">
                            <span>{button}</span>
                          </Tooltip>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </Stack>
    </Paper>
  );
}

function UserConversations({ userId, onBack }: { userId: string; onBack: () => void }) {
  const conversationsQuery = api.admin.listConversations.useQuery({
    userId,
    limit: CONVERSATION_LIMIT,
  });
  const emailsQuery = api.admin.getUserEmails.useQuery({ userIds: [userId] });
  const usersQuery = api.users.list.useQuery();

  const owner =
    emailsQuery.data?.[userId] ??
    usersQuery.data?.find((row) => row.userId === userId)?.email ??
    userId;
  const conversations: Conversation.InspectorListing[] = conversationsQuery.data ?? [];
  // One batched ledger read for every listed row (the list limit is within the 200-id cap).
  const conversationIds = conversations.map((conversation) => conversation._id);
  const creditsQuery = api.usage.credits.useQuery(
    { conversationIds },
    { enabled: conversationIds.length > 0 },
  );

  return (
    <Paper withBorder p="md">
      <Stack gap="md">
        <Group justify="space-between" wrap="wrap">
          <Group gap="md" wrap="wrap">
            <Group gap={4} wrap="nowrap">
              <Text fw={600}>{owner}</Text>
              <CopyIdButton value={userId} label="user id" />
            </Group>
            <UserOrganisation userId={userId} />
          </Group>
          <Button
            variant="subtle"
            size="compact-sm"
            leftSection={<IconArrowLeft size={14} />}
            onClick={onBack}
          >
            Choose another user
          </Button>
        </Group>
        <UserStatsStrip userId={userId} />
        <QuotaCard
          userId={userId}
          actions={<EditLimitsButton target={{ scope: 'user', subject: userId, label: owner }} />}
        />

        {conversationsQuery.isError ? (
          <Alert color="red" title="Could not load conversations">
            {conversationsQuery.error.message}
          </Alert>
        ) : conversationsQuery.isLoading ? (
          <Skeleton height={160} />
        ) : conversations.length === 0 ? (
          <Text c="dimmed" size="sm">
            No conversations for this user.
          </Text>
        ) : (
          <>
            {conversations.length >= CONVERSATION_LIMIT && (
              <Text size="xs" c="dimmed">
                Showing the {CONVERSATION_LIMIT} most recently updated conversations.
              </Text>
            )}
            <ConversationList
              conversations={conversations}
              credits={creditsSourceOf(creditsQuery)}
            />
          </>
        )}
      </Stack>
    </Paper>
  );
}

/**
 * @desc Every conversation of one organisation's members, newest first under the same list limit,
 * each row naming its owner. Members are resolved to portal users server-side
 * (`admin.listConversations({ orgId })`); a member who has never signed in has none to list.
 */
function OrganisationConversations({ orgId, onBack }: { orgId: string; onBack: () => void }) {
  const conversationsQuery = api.admin.listConversations.useQuery({
    orgId,
    limit: CONVERSATION_LIMIT,
  });
  const organisationsQuery = api.organisations.list.useQuery();
  const name = organisationsQuery.data?.find((org) => org.id === orgId)?.name ?? orgId;

  const conversations: Conversation.InspectorListing[] = conversationsQuery.data ?? [];
  const ownerIds = [...new Set(conversations.map((conversation) => conversation.refs.userId))];
  const emailsQuery = api.admin.getUserEmails.useQuery(
    { userIds: ownerIds },
    { enabled: ownerIds.length > 0 },
  );
  const conversationIds = conversations.map((conversation) => conversation._id);
  const creditsQuery = api.usage.credits.useQuery(
    { conversationIds },
    { enabled: conversationIds.length > 0 },
  );

  return (
    <Paper withBorder p="md">
      <Stack gap="md">
        <Group justify="space-between" wrap="wrap">
          <Group gap="md" wrap="wrap">
            <Group gap={4} wrap="nowrap">
              <IconBuilding size={16} aria-hidden />
              <Text fw={600}>{name}</Text>
              <CopyIdButton value={orgId} label="organisation id" />
            </Group>
            <Text size="sm" c="dimmed">
              Conversations of every member
            </Text>
          </Group>
          <Button
            variant="subtle"
            size="compact-sm"
            leftSection={<IconArrowLeft size={14} />}
            onClick={onBack}
          >
            Choose a user or organisation
          </Button>
        </Group>

        {conversationsQuery.isError ? (
          <Alert color="red" title="Could not load conversations">
            {conversationsQuery.error.message}
          </Alert>
        ) : conversationsQuery.isLoading ? (
          <Skeleton height={160} />
        ) : conversations.length === 0 ? (
          <Text c="dimmed" size="sm">
            No conversations for this organisation&apos;s members.
          </Text>
        ) : (
          <>
            {conversations.length >= CONVERSATION_LIMIT && (
              <Text size="xs" c="dimmed">
                Showing the {CONVERSATION_LIMIT} most recently updated conversations across the
                organisation.
              </Text>
            )}
            <ConversationList
              conversations={conversations}
              credits={creditsSourceOf(creditsQuery)}
              owners={emailsQuery.data ?? {}}
            />
          </>
        )}
      </Stack>
    </Paper>
  );
}
